import React, { useRef, useState } from 'react';
import { CheckCircleIcon } from 'lucide-react';
import { Badge } from '../Badge';
import { Button } from '../Button';
import { Checkbox } from '../Checkbox';
import { Select } from '../Select';
import { TextInput } from '../TextInput';
interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  location: string;
  timeline: string;
  whatsappPreferred: boolean;
}
const LOCATIONS = ['Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming', 'Outside the United States'].map(location => ({
  label: location,
  value: location.toLowerCase().replaceAll(' ', '-')
}));
const TIMELINES = ['Within the next 30 days', 'Within 1 to 3 months', 'Within 3 to 6 months', 'Within 6 to 12 months', 'Just gathering information right now'].map(timeline => ({
  label: timeline,
  value: timeline.toLowerCase().replaceAll(' ', '-')
}));
const EMPTY: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  location: '',
  timeline: '',
  whatsappPreferred: false
};
const WEBHOOK_URL = 'https://services.leadconnectorhq.com/hooks/zFfvFQI9459DpBfcybiY/webhook-trigger/170c50bc-cf36-4417-b739-0614603d0eae';
const MINIMUM_COMPLETION_TIME_MS = 3_000;
const SUBMISSION_COOLDOWN_MS = 60_000;
const LAST_SUBMISSION_KEY = 'pler-smile-last-form-submission';
export function ReserveForm() {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [website, setWebsite] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const formStartedAt = useRef(Date.now());
  const update = (key: Exclude<keyof FormState, 'whatsappPreferred'>) => (value: string) => {
    setForm(current => ({
      ...current,
      [key]: value
    }));
    setErrors(current => ({
      ...current,
      [key]: undefined
    }));
  };
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError('');
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!form.firstName.trim()) next.firstName = 'Please enter your first name.';
    if (!form.lastName.trim()) next.lastName = 'Please enter your last name.';
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) next.email = 'Enter a valid email address.';
    if (form.phone.trim().length < 7) next.phone = 'Enter a phone number for the clinical team to call.';
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    if (website) {
      setSubmitted(true);
      return;
    }
    const now = Date.now();
    if (now - formStartedAt.current < MINIMUM_COMPLETION_TIME_MS) {
      setSubmitError('Please take a moment to review your details, then try again.');
      return;
    }
    const lastSubmission = Number(window.localStorage.getItem(LAST_SUBMISSION_KEY) || '0');
    if (now - lastSubmission < SUBMISSION_COOLDOWN_MS) {
      setSubmitError('Please wait a minute before sending another request.');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: `${form.firstName.trim()} ${form.lastName.trim()}`,
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          fullName: `${form.firstName.trim()} ${form.lastName.trim()}`,
          email: form.email.trim(),
          phone: form.phone.trim(),
          location: form.location,
          timeline: form.timeline,
          whatsappPreferred: form.whatsappPreferred,
          source: 'Pler Smile Dadeland 5-Day Smile Experience',
          submittedAt: new Date().toISOString()
        })
      });
      if (!response.ok) throw new Error('Webhook request failed');
      window.localStorage.setItem(LAST_SUBMISSION_KEY, String(now));
      setSubmitted(true);
    } catch {
      setSubmitError('Your request could not be sent. Please try again or contact Pler Smile directly.');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section id="reserve" className="w-full bg-[#A9DFE9]/25 py-20 lg:py-28" data-id="element-173" data-mp="ldz5f8x9mzm1">
      <div className="mx-auto max-w-2xl px-6" data-id="element-174" data-mp="1rw5t71mb0xs1">
        <div className="flex flex-col items-center text-center" data-id="element-175" data-mp="1wuz72v5i6qsq">
          <Badge variant="aqua" size="sm" className="landing-badge mb-4 uppercase tracking-wide" data-id="element-176" data-mp="1wxlb4kt6qo67">Reserve Your Diagnostic Call</Badge>
          <h2 className="text-3xl font-bold leading-tight text-[#1A1A2E] md:text-4xl" data-id="element-177" data-mp="3cdwsh7yl9pd2">
            Find out if the 5 Day Smile Experience is right for you.
          </h2>
          <p className="mt-4 max-w-xl text-lg leading-relaxed text-[#1A1A2E]/80" data-id="element-178" data-mp="37ldg2ebn109u">
            Submit your details below. The Dadeland clinical team contacts you within 24 hours for a
            free consultation.
          </p>
        </div>

        <div className="mt-10 rounded-3xl bg-white p-6 shadow-soft ring-1 ring-white/60 sm:p-8" data-id="element-179" data-mp="2tnp2zsntaxfc">
          {submitted ? <div className="flex flex-col items-center py-10 text-center" data-id="element-180" data-mp="2opdupmd60uz8">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#A9DFE9]/40 text-[#1F6E7B]" data-id="element-181" data-mp="2jqu8dkunth6c">
                <CheckCircleIcon size={32} data-id="element-182" data-mp="mwz5014dqm89" />
              </span>
              <h3 className="mt-5 text-2xl font-bold text-[#1A1A2E]" data-id="element-183" data-mp="hxw6z0mthg9s">Your request is in.</h3>
              <p className="mt-3 max-w-sm text-[15px] leading-relaxed text-[#1A1A2E]/80" data-id="element-184" data-mp="2ce4gbm9qfd6q">
                Thank you, {form.firstName || 'friend'}. The Dadeland clinical team will contact you
                within 24 hours to schedule your free 30 minute diagnostic call.
              </p>
            </div> : <form onSubmit={handleSubmit} noValidate className="space-y-5" data-id="element-185" data-mp="27gl1ol94gye9">
              <div className="absolute h-px w-px overflow-hidden whitespace-nowrap" aria-hidden="true" data-id="element-186" data-mp="2v4m73lb07ypg">
                <label htmlFor="website" data-id="element-187" data-mp="304gwfkf4sg8e">Leave this field empty</label>
                <input id="website" name="website" type="text" value={website} onChange={event => setWebsite(event.target.value)} tabIndex={-1} autoComplete="off" data-id="element-188" data-mp="2m4fwq8alreui" />
              </div>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2" data-id="element-189" data-mp="1er4dy0wg65od">
                <TextInput label="First name" name="firstName" value={form.firstName} onChange={event => update('firstName')(event.target.value)} error={errors.firstName} autoComplete="given-name" data-id="element-190" data-mp="177cyykfe2geq" />
                <TextInput label="Last name" name="lastName" value={form.lastName} onChange={event => update('lastName')(event.target.value)} error={errors.lastName} autoComplete="family-name" data-id="element-191" data-mp="1h5ixcs1zl4eu" />
              </div>
              <TextInput label="Email" name="email" type="email" placeholder="you@example.com" value={form.email} onChange={event => update('email')(event.target.value)} error={errors.email} autoComplete="email" data-id="element-192" data-mp="ogc15zq08qwr" />
              <TextInput label="Phone number" name="phone" type="tel" value={form.phone} onChange={event => update('phone')(event.target.value)} error={errors.phone} autoComplete="tel" data-id="element-193" data-mp="y6x0xqhf01h9" />
              <Select label="Where are you currently located?" name="location" placeholder="Select your state" options={LOCATIONS} value={form.location} onChange={event => update('location')(event.target.value)} data-id="element-194" data-mp="5pueocvrmrju" />
              <Select label="When are you hoping to have this done?" name="timeline" placeholder="Select a timeframe" options={TIMELINES} value={form.timeline} onChange={event => update('timeline')(event.target.value)} data-id="element-195" data-mp="3bedpyvphl20u" />
              <Checkbox name="whatsappPreferred" label="WhatsApp preferred" checked={form.whatsappPreferred} onCheckedChange={checked => setForm(current => ({
            ...current,
            whatsappPreferred: checked
          }))} data-id="element-196" data-mp="3la8do58khnah" />
              <Button type="submit" size="lg" className="w-full" disabled={isSubmitting} aria-busy={isSubmitting} data-id="element-197" data-mp="34luxfzzh91u">Reserve My Free Consultation</Button>
              {submitError ? <p role="alert" className="text-center text-sm font-medium text-red-700" data-id="element-198" data-mp="cw83f4kxsu0w">{submitError}</p> : null}
              <div className="space-y-1 text-center text-sm leading-relaxed text-[#1A1A2E]/70" data-id="element-199" data-mp="gy388j22tpjf">
                <p className="italic" data-id="element-200" data-mp="lvmx0cwehkpm">Within 24 hours, the Dadeland clinical team contacts you with your custom consultation plan.</p>
                <p data-id="element-201" data-mp="26fqm1ikopqy9">If it is not the fit you hoped for, your deposit is fully refundable for 7 days.</p>
              </div>
            </form>}
        </div>
      </div>
    </section>;
}